import { useEffect, useRef, useState } from 'react';
import type { VideoBackgroundEffect } from '../types/meeting.types.ts';
import { createCanvasFallbackStream } from '../utils/canvasBackgroundFallback.ts';
import { getPersonSegmenter } from '../utils/personSegmentation.ts';
import { createProcessedVideoStream } from '../utils/videoBackgroundProcessor.ts';
import { CSP_FIX_MESSAGE, isCspWasmError } from '../utils/wasmPolicy.ts';

export type VideoBackgroundEffectState = {
  stream: MediaStream | null;
  isLoading: boolean;
  error: string | null;
  usesSegmentation: boolean;
};

export function useVideoBackgroundEffect(
  sourceStream: MediaStream | null,
  effect: VideoBackgroundEffect,
  enabled: boolean
): VideoBackgroundEffectState {
  const [outputStream, setOutputStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usesSegmentation, setUsesSegmentation] = useState(false);
  const processorRef = useRef<{ stop: () => void } | null>(null);

  useEffect(() => {
    const stopProcessor = () => {
      processorRef.current?.stop();
      processorRef.current = null;
    };

    if (!enabled || !sourceStream || effect === 'none') {
      stopProcessor();
      setIsLoading(false);
      setError(null);
      setUsesSegmentation(false);
      setOutputStream(sourceStream);
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = sourceStream;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setUsesSegmentation(false);
    setOutputStream(null);

    const waitForVideoFrame = () =>
      new Promise<void>((resolve) => {
        const tick = () => {
          if (cancelled) return;
          if (video.videoWidth > 0 && video.readyState >= 2) {
            resolve();
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      });

    const startCanvasFallback = async (message: string | null) => {
      await waitForVideoFrame();
      if (cancelled) return;

      stopProcessor();
      const fallback = createCanvasFallbackStream(video, effect, true);
      if (!fallback) {
        throw new Error('Yedek video işleyici başlatılamadı');
      }

      processorRef.current = fallback;
      setOutputStream(fallback.stream);
      setUsesSegmentation(false);
      setError(message);
      setIsLoading(false);
    };

    const startSegmentation = async () => {
      try {
        await video.play();
      } catch {
        /* autoplay */
      }

      try {
        const segmenter = await getPersonSegmenter();
        if (cancelled) return;

        await waitForVideoFrame();
        if (cancelled) return;

        stopProcessor();
        const processed = createProcessedVideoStream(video, effect, segmenter, true);
        if (!processed) {
          throw new Error('Video işleyici başlatılamadı');
        }

        processorRef.current = processed;
        setOutputStream(processed.stream);
        setUsesSegmentation(true);
        setError(null);
        setIsLoading(false);
      } catch (loadError) {
        console.error('Arka plan efekti başlatılamadı:', loadError);
        if (cancelled) return;

        const fallbackMessage = isCspWasmError(loadError)
          ? `${CSP_FIX_MESSAGE} Şimdilik basit efekt kullanılıyor.`
          : 'Kişi tanıma kullanılamadı; basit efekt kullanılıyor.';

        try {
          await startCanvasFallback(fallbackMessage);
        } catch (fallbackError) {
          console.error('Yedek efekt de başarısız:', fallbackError);
          setOutputStream(sourceStream);
          setError(CSP_FIX_MESSAGE);
          setIsLoading(false);
        }
      }
    };

    void startSegmentation();

    return () => {
      cancelled = true;
      stopProcessor();
      video.srcObject = null;
    };
  }, [sourceStream, effect, enabled]);

  if (!enabled || !sourceStream || effect === 'none') {
    return { stream: sourceStream, isLoading: false, error: null, usesSegmentation: false };
  }

  return {
    stream: outputStream ?? sourceStream,
    isLoading,
    error,
    usesSegmentation,
  };
}
